import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, CreditCard, Download, CheckCircle, AlertCircle,
  Clock, Trash2, Plus, Settings, History, DollarSign, Calendar
} from 'lucide-react';
import { billingApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_STYLES = {
  paid:               { label: 'Paid',        color: '#16a34a', bg: '#dcfce7' },
  sent:               { label: 'Due',         color: '#ca8a04', bg: '#fef9c3' },
  past_due:           { label: 'Past Due',    color: '#dc2626', bg: '#fee2e2' },
  auto_charge_failed: { label: 'Payment Failed', color: '#dc2626', bg: '#fee2e2' },
  partially_paid:     { label: 'Partial',     color: '#ea580c', bg: '#ffedd5' },
  voided:             { label: 'Voided',      color: '#6b7280', bg: '#f3f4f6' },
};

function Section({ title, icon: Icon, children, extra }) {
  return (
    <section className="billing-section">
      <div className="billing-section-header">
        <h2><Icon size={18} /> {title}</h2>
        {extra}
      </div>
      {children}
    </section>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState({ methods: [], mode: 'live' });
  const [autoPay, setAutoPay] = useState({ enabled: false });
  const [payments, setPayments] = useState([]);
  const [credit, setCredit] = useState({ balance: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState(null);
  const [year] = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Feature flag — bail early + redirect if billing is currently disabled
      const enabled = await billingApi.isEnabled();
      if (!enabled) {
        navigate('/');
        return;
      }
      const [i, pm, ap, ph, cr] = await Promise.all([
        billingApi.listInvoices(),
        billingApi.listPaymentMethods(),
        billingApi.getAutoPay(),
        billingApi.listPayments(),
        billingApi.getCredit(),
      ]);
      setInvoices(i);
      setPaymentMethods(pm);
      setAutoPay(ap);
      setPayments(ph);
      setCredit(cr || { balance: 0 });
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Post-checkout confirmation — Stripe returns to /billing?paid=1 (or
  // ?pm_added=1). Show a clear banner so the client KNOWS it went through
  // (the missing confirmation is what made people re-pay), then strip the query.
  useEffect(() => {
    if (searchParams.get('paid') === '1') {
      setConfirmMsg("Payment received — it's now processing. Bank (ACH) payments take a few business days to settle; no further action needed.");
      navigate('/billing', { replace: true });
    } else if (searchParams.get('pm_added') === '1') {
      setConfirmMsg('Payment method added.');
      navigate('/billing', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePay = async (invoiceId, method) => {
    try {
      const res = await billingApi.startCheckout(invoiceId, method);
      if (res?.url) window.location.href = res.url;
    } catch (e) {
      alert('Could not start checkout: ' + e.message);
    }
  };

  // Deep-link handling for the past-due SMS reminder links:
  //   ?action=pay&invoice=<id>&method=<ach|card>  → auto-start Checkout once
  //   ?invoice=<id>                               → scroll to + highlight it
  // The auto-start only fires for an invoice that's still open with a balance,
  // and only once, so a refresh/back never re-charges.
  const [searchParams] = useSearchParams();
  const highlightInvoiceId = searchParams.get('invoice');
  const autoPayFiredRef = useRef(false);

  useEffect(() => {
    if (loading || autoPayFiredRef.current) return;
    const action = searchParams.get('action');
    const invoiceId = searchParams.get('invoice');
    const method = searchParams.get('method');
    if (action === 'pay' && invoiceId && method) {
      autoPayFiredRef.current = true;  // fire at most once regardless of outcome
      const inv = invoices.find(x => String(x.id) === String(invoiceId));
      const balance = inv ? Number(inv.total || 0) - Number(inv.amount_paid || 0) : 0;
      if (inv && balance > 0 && !inv.payment_pending) handlePay(invoiceId, method);
    } else if (invoiceId) {
      // No auto-pay (no saved method) — just bring the invoice into view.
      const el = document.getElementById(`inv-${invoiceId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoices]);

  const handleAddPaymentMethod = async () => {
    try {
      const { url } = await billingApi.createSetupSession();
      window.location.href = url;
    } catch (e) {
      alert('Could not start setup: ' + e.message);
    }
  };

  const handleRemovePm = async (pmId) => {
    if (!window.confirm('Remove this saved payment method?')) return;
    try {
      await billingApi.removePaymentMethod(pmId);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleSetDefault = async (pmId) => {
    try {
      await billingApi.setDefaultPaymentMethod(pmId);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleToggleAutoPay = async () => {
    try {
      const res = await billingApi.setAutoPay(!autoPay.enabled);
      setAutoPay({ enabled: res.enabled });
    } catch (e) { alert(e.message); }
  };

  const handleDispute = async (invoiceId) => {
    const reason = window.prompt('What would you like to dispute about this invoice?');
    if (!reason || !reason.trim()) return;
    try {
      await billingApi.openDispute(invoiceId, reason.trim());
      alert('Dispute submitted. Our team will review and respond.');
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) return <div className="app-loading">Loading billing…</div>;

  const dueInvoices = invoices.filter(i => ['sent', 'past_due', 'partially_paid', 'auto_charge_failed'].includes(i.status));
  const paidInvoices = invoices.filter(i => i.status === 'paid');

  return (
    <div className="billing-page">
      <div className="billing-topbar">
        <h1>Billing</h1>
      </div>

      {error && <div className="billing-error">{error}</div>}
      {confirmMsg && (
        <div style={{ margin: '0 0 12px', padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#065f46', fontSize: 14 }}>
          {confirmMsg}
        </div>
      )}
      {credit.balance > 0 && (
        <div style={{ margin: '0 0 12px', padding: '12px 16px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, color: '#3730a3', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14 }}>Account credit available — applied automatically to your invoices.</span>
          <strong style={{ fontSize: 18 }}>${Number(credit.balance).toFixed(2)}</strong>
        </div>
      )}

      {/* Outstanding invoices */}
      <Section title="Due Invoices" icon={AlertCircle}>
        {dueInvoices.length === 0 ? (
          <div className="billing-empty">
            <CheckCircle size={24} color="#16a34a" /> All caught up — no invoices due.
          </div>
        ) : (
          <div className="invoice-list">
            {dueInvoices.map(inv => {
              // An in-flight (ACH settling) payment shows as "Processing"
              // instead of the underlying DUE/past-due status.
              const s = inv.payment_pending
                ? { label: 'Processing', color: '#1e40af', bg: '#dbeafe' }
                : (STATUS_STYLES[inv.status] || { label: inv.status, color: '#333', bg: '#f3f4f6' });
              const defaultPm = paymentMethods.methods.find(pm => pm.is_default_autopay);
              const showAutoPay = autoPay.enabled && defaultPm && inv.status !== 'auto_charge_failed';
              const pmDesc = defaultPm
                ? defaultPm.pm_type === 'card'
                  ? `${defaultPm.brand || 'card'}${defaultPm.last4 ? ` ending in •••${defaultPm.last4}` : ''}`
                  : `bank account${defaultPm.brand ? ` (${defaultPm.brand})` : ''}${defaultPm.last4 ? ` ending in •••${defaultPm.last4}` : ''}`
                : '';
              const isHighlighted = highlightInvoiceId && String(inv.id) === String(highlightInvoiceId);
              return (
                <div
                  key={inv.id}
                  id={`inv-${inv.id}`}
                  className="invoice-card"
                  style={isHighlighted ? { boxShadow: '0 0 0 2px #3182ce', borderRadius: 8 } : undefined}
                >
                  <div className="invoice-card-main">
                    <div className="invoice-number">{inv.invoice_number}</div>
                    <div className="invoice-meta">
                      Issued {inv.issue_date?.slice(0, 10)} · Due {inv.due_date?.slice(0, 10)}
                    </div>
                    <span className="invoice-status" style={{ color: s.color, background: s.bg }}>{s.label}</span>
                    {showAutoPay && (
                      <div style={{
                        marginTop: 8, padding: '8px 10px',
                        background: '#ecfdf5', border: '1px solid #a7f3d0',
                        borderRadius: 6, fontSize: 12, color: '#065f46',
                      }}>
                        <strong>Auto-pay enabled</strong> — we'll charge your {pmDesc} on{' '}
                        <strong>{inv.due_date?.slice(0, 10)}</strong> for{' '}
                        <strong>${(inv.total || 0).toFixed(2)}</strong>.
                      </div>
                    )}
                    {inv.payment_pending && (
                      <div style={{
                        marginTop: 8, padding: '8px 10px',
                        background: '#eff6ff', border: '1px solid #bfdbfe',
                        borderRadius: 6, fontSize: 12, color: '#1e40af',
                      }}>
                        <strong>Payment processing</strong> — your payment of{' '}
                        <strong>${(inv.pending_amount || inv.total || 0).toFixed(2)}</strong> was received and is
                        clearing. Bank (ACH) payments take a few business days to settle — no further action needed.
                      </div>
                    )}
                  </div>
                  <div className="invoice-card-amount">
                    <div className="invoice-total">${(inv.total || 0).toFixed(2)}</div>
                    {inv.amount_paid > 0 && <div className="invoice-paid-sub">Paid ${(inv.amount_paid).toFixed(2)}</div>}
                  </div>
                  <div className="invoice-card-actions">
                    {!inv.payment_pending && (
                      <>
                        <button className="pay-btn pay-ach" onClick={() => handlePay(inv.id, 'ach')}>
                          {showAutoPay ? 'Pay early with Bank (ACH)' : 'Pay with Bank (ACH)'}
                        </button>
                        <button className="pay-btn pay-card" onClick={() => handlePay(inv.id, 'card')}>
                          {showAutoPay ? 'Pay early with Card' : 'Pay with Card'}
                        </button>
                      </>
                    )}
                    <div className="invoice-card-extras">
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => billingApi.openInvoicePdf(inv.id).catch(e => alert(e.message))}
                      >
                        <Download size={14} /> PDF
                      </button>
                      <button className="link-btn" onClick={() => handleDispute(inv.id)}>
                        Dispute
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Payment methods + auto-pay */}
      <Section
        title="Saved Payment Methods"
        icon={CreditCard}
        extra={<button className="primary-btn" onClick={handleAddPaymentMethod}><Plus size={14}/> Add Payment Method</button>}
      >
        {paymentMethods.mode === 'test' && (
          <div className="billing-test-notice">Test mode — no real charges will be made.</div>
        )}
        {paymentMethods.methods.length === 0 ? (
          <div className="billing-empty">No saved payment methods. Add one to enable auto-pay.</div>
        ) : (
          <ul className="pm-list">
            {paymentMethods.methods.map(pm => (
              <li key={pm.id} className={`pm-item ${pm.is_default_autopay ? 'pm-default' : ''}`}>
                <div className="pm-info">
                  <strong>{pm.pm_type === 'card' ? 'Card' : 'Bank Account'}</strong>
                  {pm.brand && ` · ${pm.brand}`}
                  {pm.last4 && ` ••${pm.last4}`}
                  {pm.pm_type === 'card' && pm.exp_month && <span className="pm-exp"> (exp {pm.exp_month}/{String(pm.exp_year).slice(-2)})</span>}
                  {pm.is_default_autopay && <span className="pm-default-tag">Default for auto-pay</span>}
                </div>
                <div className="pm-actions">
                  {!pm.is_default_autopay && (
                    <button className="link-btn" onClick={() => handleSetDefault(pm.id)}>Set as default</button>
                  )}
                  <button className="link-btn danger" onClick={() => handleRemovePm(pm.id)}>
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="auto-pay-row">
          <div>
            <strong>Auto-pay</strong>
            <div className="auto-pay-help">
              Automatically charge your default payment method when an invoice is due.
              {paymentMethods.methods.some(pm => pm.is_default_autopay && pm.pm_type === 'card') &&
                ' Note: card payments include a processing fee.'}
            </div>
          </div>
          <label className={`toggle-switch ${autoPay.enabled ? 'is-on' : 'is-off'}`}>
            <input
              type="checkbox"
              checked={autoPay.enabled}
              onChange={handleToggleAutoPay}
              disabled={paymentMethods.methods.length === 0}
            />
            {/* Real sliding switch. The native checkbox is kept (visually
                hidden) for keyboard/screen-reader support; a bare checkbox
                next to the word OFF read as "tick to enable" and left people
                unsure whether auto-pay was actually on. */}
            <span className="toggle-track" aria-hidden="true"><span className="toggle-knob" /></span>
            <span className="toggle-state">{autoPay.enabled ? 'ON' : 'OFF'}</span>
          </label>
        </div>
      </Section>

      {/* Paid history */}
      <Section title="Payment History" icon={History}>
        {payments.length === 0 ? (
          <div className="billing-empty">No payments recorded yet.</div>
        ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th><th>Invoice</th><th>Method</th><th className="right">Amount</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p.id}>
                  <td>{p.charged_at?.slice(0, 10)}</td>
                  <td>{p.invoice_number}</td>
                  <td>
                    {p.method_type === 'card' ? 'Card'
                      : p.method_type === 'credit' ? 'Account credit'
                      : p.method_type === 'us_bank_account' ? 'Bank'
                      : p.method_type === 'check' ? 'Check'
                      : p.method_type === 'wire' ? 'Wire'
                      : 'Bank'}
                    {p.method_last4 ? ` ••${p.method_last4}` : ''}
                  </td>
                  <td className="right">${(p.amount || 0).toFixed(2)}</td>
                  <td>{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Paid invoices */}
      {paidInvoices.length > 0 && (
        <Section title="Paid Invoices" icon={CheckCircle}>
          <table className="billing-table">
            <thead>
              <tr>
                <th>Invoice</th><th>Issued</th><th>Paid</th><th className="right">Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {paidInvoices.map(inv => (
                <tr key={inv.id}>
                  <td>{inv.invoice_number}</td>
                  <td>{inv.issue_date?.slice(0, 10)}</td>
                  <td>{inv.paid_at?.slice(0, 10)}</td>
                  <td className="right">${(inv.total || 0).toFixed(2)}</td>
                  <td>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => billingApi.openInvoicePdf(inv.id).catch(e => alert(e.message))}
                      title="Download PDF"
                    >
                      <Download size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Annual summary */}
      <Section title="Annual Summary" icon={Calendar}>
        <div className="annual-summary-row">
          <span>Download a {year} summary PDF for your records:</span>
          <a className="primary-btn" href={billingApi.annualSummaryPdfUrl(year)} target="_blank" rel="noopener noreferrer">
            <Download size={14} /> Download {year}
          </a>
        </div>
      </Section>
    </div>
  );
}
